import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { format, isBefore, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from './AdminNavbar';
import AssignmentService from '../../services/AssignmentService';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import EditAssignmentDialog from '../dialogs/EditAssignmentDialog';
import AdminPageWrapper from './AdminPageWrapper';
import { assignmentManagementHelpItems } from '../../utils/helpItems';

const AssignmentDashboard = ({ darkMode, toggleDarkMode, themeVariant }) => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [success, setSuccess] = useState(null);
  const [videos, setVideos] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  
  // Add sorting state
  const [orderBy, setOrderBy] = useState('dueDate');
  const [order, setOrder] = useState('asc');
  
  useEffect(() => {
    fetchAssignments();
  }, [tabValue]);
  
  const fetchAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      let response;
      if (tabValue === 0) {
        response = await AssignmentService.getAllAssignments(authUser.email, authToken);
      } else if (tabValue === 1) {
        response = await AssignmentService.getAssignedByMe(authUser.email, authToken);
      } else if (tabValue === 2) {
        response = await AssignmentService.getAssignmentsByStatus('OVERDUE', authUser.email, authToken);
      }
      
      if (response && response.data) {
        setAssignments(response.data);
        
        // Get unique video IDs
        const videoIds = [...new Set(response.data.map(a => a.videoId))];
        if (videoIds.length > 0) {
          fetchVideoDetails(videoIds, authUser.email, authToken);
        }
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
      setError("Failed to fetch assignments: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  const fetchVideoDetails = async (videoIds, email, authToken) => {
    try {
      const response = await videoCRUDServiceInstance.getVideoDataOfIds(
        videoIds.join(','),
        email,
        authToken
      );
      
      if (response && response.data) {
        // Create a lookup object by ID
        const videosById = {};
        response.data.forEach(video => {
          videosById[video.id] = video;
        });
        
        setVideos(videosById);
      }
    } catch (error) {
      console.error("Error fetching video details:", error);
    }
  };
  
  const handleDeleteAssignment = async (assignmentId) => {
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      await AssignmentService.deleteAssignment(
        assignmentId,
        authUser.email,
        authToken
      );
      
      setSuccess("Assignment deleted successfully");
      
      // Refresh the assignments list
      fetchAssignments();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error("Error deleting assignment:", error);
      setError("Failed to delete assignment: " + (error.response?.data?.message || error.message));
    }
  };
  
  const handleUpdateStatus = async (assignmentId, newStatus) => {
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      await AssignmentService.updateAssignmentStatus(
        assignmentId,
        newStatus,
        authUser.email,
        authToken
      );
      
      setSuccess(`Assignment marked as ${newStatus.toLowerCase()}`);
      
      // Refresh the assignments list
      fetchAssignments();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error("Error updating assignment status:", error);
      setError("Failed to update assignment: " + (error.response?.data?.message || error.message));
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleEditAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setEditDialogOpen(true);
  };
  
  // Add this new function for handling sort requests
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  // Function to compare values for sorting
  const descendingComparator = (a, b, orderBy) => {
    // Handle special cases for different properties
    if (orderBy === 'dueDate') {
      const dateA = a[orderBy] ? new Date(a[orderBy]).getTime() : 0;
      const dateB = b[orderBy] ? new Date(b[orderBy]).getTime() : 0;
      return dateB - dateA;
    }
    
    // For status, use a custom order
    if (orderBy === 'status') {
      const statusOrder = { 'COMPLETED': 0, 'IN_PROGRESS': 1, 'ASSIGNED': 2, 'OVERDUE': 3 };
      return statusOrder[b[orderBy] || 'ASSIGNED'] - statusOrder[a[orderBy] || 'ASSIGNED'];
    }
    
    // For normal string properties
    if (!b[orderBy]) return -1;
    if (!a[orderBy]) return 1;
    
    return b[orderBy].toString().localeCompare(a[orderBy].toString());
  };
  
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };
  
  // Filter assignments based on search term and status filter
  const filteredAssignments = assignments.filter(assignment => {
    // Search term filter
    const searchTermLower = searchTerm.toLowerCase();
    const matchesSearch = 
      assignment.videoName?.toLowerCase().includes(searchTermLower) ||
      assignment.languageName?.toLowerCase().includes(searchTermLower) ||
      assignment.assignedTo?.toLowerCase().includes(searchTermLower) ||
      assignment.assignedBy?.toLowerCase().includes(searchTermLower);
      
    // Status filter
    const matchesStatus = 
      statusFilter === 'ALL' || 
      assignment.status === statusFilter;
      
    return matchesSearch && matchesStatus;
  });
  
  // Sort the filtered assignments
  const sortedAssignments = React.useMemo(() => {
    return [...filteredAssignments].sort(getComparator(order, orderBy));
  }, [filteredAssignments, order, orderBy]);
  
  // Paginate sorted assignments
  const paginatedAssignments = sortedAssignments
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return dateString;
    }
  };
  
  const getStatusColor = (status, dueDate) => {
    if (status === 'COMPLETED') return 'success';
    if (status === 'IN_PROGRESS') return 'primary';
    if (status === 'OVERDUE' || (dueDate && isBefore(parseISO(dueDate), new Date()))) return 'error';
    return 'warning'; // ASSIGNED
  };
  
  const getVideoUrl = (videoId, language) => {
    // This is a placeholder - update with your actual URL structure
    return `/videos/edit?id=${videoId}&lang=${language}`;
  };

  const getCategoryChipColor = (category) => {
    switch(category) {
      case 'Level 1': return '#2196f3';  // Blue
      case 'Level 2': return '#9c27b0';  // Purple
      case 'Level 3': return '#3f51b5';  // Indigo
      default: return '#757575';         // Gray
    }
  };

  // Create a sortable table header cell component
  const SortableTableCell = ({ id, label, disableSorting = false }) => {
    return (
      <TableCell sortDirection={orderBy === id ? order : false}>
        {disableSorting ? (
          label
        ) : (
          <TableSortLabel
            active={orderBy === id}
            direction={orderBy === id ? order : 'asc'}
            onClick={() => handleRequestSort(id)}
          >
            {label}
          </TableSortLabel>
        )}
      </TableCell>
    );
  };

  return (
    <AdminPageWrapper
      title="Assignment Management"
      helpItems={assignmentManagementHelpItems}
      darkMode = {darkMode}
      toggleDarkMode = {toggleDarkMode}
      themeVariant={themeVariant}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }} className="assignments-management-header">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AssignmentIcon sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h4">Assignment Management</Typography>
        </Box>
        <Button 
          startIcon={<ArrowBackIcon />} 
          sx={{ ml: 'auto' }}
          onClick={() => navigate('/admin')}
        >
          Back to Dashboard
        </Button>
      </Box>
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ mb: 3 }} className="assignment-tabs">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<AssignmentIcon />} 
            label="All Assignments" 
            iconPosition="start"
          />
          <Tab 
            icon={<FilterListIcon />} 
            label="Assigned by Me" 
            iconPosition="start"
          />
          <Tab 
            icon={<ErrorIcon />} 
            label="Overdue" 
            iconPosition="start"
          />
        </Tabs>
        
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }} className="assignment-filters">
          <TextField
            placeholder="Search assignments..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              <MenuItem value="ASSIGNED">Assigned</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="OVERDUE">Overdue</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchAssignments()}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : paginatedAssignments.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No assignments found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || statusFilter !== 'ALL' 
                ? 'Try adjusting your filters'
                : 'Start by assigning videos to users'}
            </Typography>
          </Box>
        ) : (
          <Box className="assignment-list">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableTableCell id="videoName" label="Video" />
                    <SortableTableCell id="languageName" label="Language" />
                    <SortableTableCell id="assignedTo" label="Assigned To" />
                    <SortableTableCell id="assignedBy" label="Assigned By" />
                    <SortableTableCell id="dueDate" label="Due Date" />
                    <SortableTableCell id="status" label="Status" />
                    <SortableTableCell id="category" label="Category" />
                    <SortableTableCell id="actions" label="Actions" disableSorting={true} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAssignments.map((assignment) => {
                    const video = videos[assignment.videoId];
                    const isOverdue = assignment.dueDate && 
                      isBefore(parseISO(assignment.dueDate), new Date()) &&
                      assignment.status !== 'COMPLETED';
                      
                    const statusColor = getStatusColor(assignment.status, assignment.dueDate);
                    
                    return (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {assignment.videoName || video?.videoName || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>{assignment.languageName}</TableCell>
                        <TableCell>{assignment.assignedTo}</TableCell>
                        <TableCell>{assignment.assignedBy}</TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color={isOverdue ? 'error' : 'inherit'}
                            sx={{ fontWeight: isOverdue ? 'bold' : 'normal' }}
                          >
                            {formatDate(assignment.dueDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={assignment.status || 'ASSIGNED'}
                            color={statusColor}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={assignment.category || 'Unspecified'} 
                            size="small"
                            sx={{ 
                              backgroundColor: `${getCategoryChipColor(assignment.category)}20`,
                              color: getCategoryChipColor(assignment.category),
                              borderColor: getCategoryChipColor(assignment.category),
                              fontWeight: 500
                            }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex' }}>
                            {assignment.status !== 'COMPLETED' && (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleUpdateStatus(assignment.id, 'COMPLETED')}
                                title="Mark as completed"
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            )}
                            
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditAssignment(assignment)}
                              title="Edit assignment"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteAssignment(assignment.id)}
                              title="Delete assignment"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredAssignments.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        )}
      </Paper>
      
      <EditAssignmentDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        assignment={selectedAssignment}
        onUpdate={fetchAssignments}
        className="edit-assignment-dialog"
      />
    </AdminPageWrapper>
  );
};

export default AssignmentDashboard;